import { Grid } from '@mui/material';
import { useSearchQueryParam } from '@piaas/piaas-ui-toolkit/hooks';
import { ODSCardBasic, ODSDivider } from '@telekom-ods/react-ui-kit';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Auth from '../../api/auth';
import Alert from '../../components/Alert';
import BreadCrumb from '../../components/BreadCrumb';
import { ScrollToTop } from '../../components/ScrollToTop/ScrollToTop';
import Table from '../../components/Table';
import useApiQuery from '../../hooks/useApiQuery';
import useTableApiCall from '../../hooks/useTableApiCall';
import ProductDetails from '../Products/UserProducts/ProductDetails';

import ReportStyle from './Reports.module.css';

import type { APIHookArg } from '@/types';
import type { JuiceGroup } from '@/types/juicegroupTypes';
import type { Product } from '@/types/productTypes';

const JuiceGroups = () => {
  const intl = useIntl();
  const [persistedProductId, setPersistedProductId] = useSearchQueryParam(
    'selectedJuiceGroupProduct'
  );
  const [selectedJuiceGroup, setSelectedJuiceGroup] = useState({
    juiceGroup: '',
    productId: persistedProductId,
    osProjectName: '',
  });
  const [productDetails, setProductDetails] = useState<Product | null>(null);
  const { makeApiCall: getProductById } = useApiQuery(
    Auth.fetchV2ProductById as unknown as APIHookArg
  );

  const [rows, setRows] = useState<JuiceGroup[]>([]);
  // Field ids double as the advanced-search query params sent to the API.
  const fields = [
    { title: { id: 'juiceGroup' }, id: 'juiceGroup', isAdvancedSearchable: true },
    { title: { id: 'productNameColumn' }, id: 'productName', isAdvancedSearchable: true },
    { title: { id: 'type' }, id: 'type', isAdvancedSearchable: true },
    { title: { id: 'projectNameColumn' }, id: 'projectName', isAdvancedSearchable: true },
    { title: { id: 'organization' }, id: 'organization', isAdvancedSearchable: true },
  ];

  const [initialSearch] = useSearchQueryParam('juiceGroupsSearch');

  const { setCurrentPage, itemsPerPage, setItemsPerPage, ...tableData } = useTableApiCall(
    Auth.fetchV2JuiceGroups as unknown as APIHookArg,
    {
      isPaginated: true,
      setRows: setRows as (rows: unknown[]) => void,
      searchColumns: ['details.juiceGroup', 'type', 'name', 'organizationId', 'projectId'],
      initialFilter: initialSearch,
      searchParamKey: 'juiceGroupsSearch',
      initialItemsPerPage: 5,
    }
  );

  // Transform error to match TableProps type
  const transformedError = tableData.error ? tableData.error.message : null;

  // Fetch product details when juice group is selected
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (selectedJuiceGroup.productId) {
        const data = (await getProductById(selectedJuiceGroup.productId)) as Product;

        setProductDetails(data);
      }
    };

    if (selectedJuiceGroup.productId) {
      fetchProductDetails();
    }
  }, [getProductById, selectedJuiceGroup.productId]);

  const showDetails = Boolean(
    selectedJuiceGroup.productId && productDetails && Object.keys(productDetails).length > 0
  );

  // Waits for the expanded layout to actually commit - scrolling any sooner
  // targets the still-collapsed card on a first-ever selection.
  useEffect(() => {
    if (showDetails) {
      document.getElementById('product-details')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showDetails, productDetails]);

  // Backfills `juiceGroup`/`osProjectName` (needed for the row highlight)
  // once `rows` loads, for a selection restored from the URL above - a row
  // click sets all three together, but the URL only carries the product id.
  useEffect(() => {
    if (!selectedJuiceGroup.productId || selectedJuiceGroup.juiceGroup) {
      return;
    }

    const restoredRow = rows.find(row => row.productId === selectedJuiceGroup.productId);
    if (restoredRow) {
      setSelectedJuiceGroup({
        juiceGroup: restoredRow.juiceGroup,
        productId: restoredRow.productId,
        osProjectName: restoredRow.projectName,
      });
    }
  }, [rows, selectedJuiceGroup.productId, selectedJuiceGroup.juiceGroup]);

  const transformedRows = rows.map(juiceGroup => ({
    key: juiceGroup.juiceGroup,
    onRowClick: () => {
      setPersistedProductId(juiceGroup.productId);
      setSelectedJuiceGroup({
        juiceGroup: juiceGroup.juiceGroup,
        productId: juiceGroup.productId,
        osProjectName: juiceGroup.projectName,
      });
    },
    data: [
      { field: 'juiceGroup', value: juiceGroup.juiceGroup },
      { field: 'productName', value: juiceGroup.productName },
      { field: 'type', value: juiceGroup.type === 'evaluation' ? 'compute' : juiceGroup.type },
      { field: 'projectName', value: juiceGroup.projectName },
      { field: 'organization', value: juiceGroup.organization.enterpriseName },
    ],
  }));

  return (
    <Grid
      container
      direction='column'
      gap={0}
    >
      <Grid item>
        <BreadCrumb
          items={[
            {
              href: '/',
              text: intl.formatMessage({ id: 'title' }),
            },
            {
              href: '#',
              text: intl.formatMessage({ id: 'juiceGroupsHeader' }),
            },
          ]}
        />
      </Grid>

      <Grid item>
        <Table
          header={intl.formatMessage({ id: 'juiceGroupsHeader' })}
          subHeader={intl.formatMessage({ id: 'juiceGroupsSubHeader' })}
          fields={fields}
          rows={transformedRows}
          {...tableData}
          error={transformedError}
          setFilter={tableData.setFilter as (filter: unknown) => void}
          onPageChange={newPage => {
            setCurrentPage(newPage);
          }}
          advancedSearch={true}
          searchParamKey='juiceGroupsSearch'
          allowSelectedHighlight={{
            selectedKey: selectedJuiceGroup.juiceGroup,
          }}
          pageSize={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
        />
      </Grid>
      <Grid
        item
        paddingY={4}
      >
        <ODSDivider
          className='ODS-tabs'
          variant='horizontal'
        />
      </Grid>
      <Grid item>
        <ODSCardBasic
          id='product-details'
          className={`${ReportStyle.report_details_card ?? ''} ${!selectedJuiceGroup.productId ? (ReportStyle.juice_details_card_without_spacing ?? '') : (ReportStyle.details_card_with_spacing ?? '')}`}
          contentSlot={
            <Grid item>
              {showDetails ? (
                <>
                  <ProductDetails
                    key={selectedJuiceGroup.productId}
                    {...(productDetails && {
                      details: productDetails,
                    })}
                    productIdProps={selectedJuiceGroup.productId}
                  />
                  <ScrollToTop
                    className={`${ReportStyle.scroll_top ?? ''} ${ReportStyle.juice_scroll_top ?? ''}`}
                    fixed={false}
                    scrollContainerClass='page-content-scrollable'
                  />
                </>
              ) : (
                <Alert
                  className={ReportStyle.select_tenant_alert ?? ''}
                  mode='error'
                  title={intl.formatMessage({ id: 'noProductSelected' })}
                  text=''
                />
              )}
            </Grid>
          }
          size='large'
          tabIndex={-1}
        />
      </Grid>
    </Grid>
  );
};

export default JuiceGroups;
